import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import * as Select from '@radix-ui/react-select';
import Plus from '@/icons/Plus';
import { eventTypesApi } from '@/api';
import { useFactTypesStore, usePersonFactsStore, usePersonLifeEventsStore } from '@/hooks/useStores';
import DateFieldWithPrecision from '@/components/shared/DateFieldWithPrecision';
import { generateUUID } from '@/utils/uuid';
import { formatDateForInput } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';
import type { SuggestFactsLifeEventsState } from '@/types/suggestion';

import '../person/person-facts.css';
import '../person/person-life-events.css';
import './suggest-facts-life-events.css';

interface SuggestFactsLifeEventsSectionProps {
  familyMemberId: string;
  value: SuggestFactsLifeEventsState;
  onChange: (value: SuggestFactsLifeEventsState) => void;
}

const SuggestFactsLifeEventsSection: React.FC<SuggestFactsLifeEventsSectionProps> = observer(
  ({ familyMemberId, value, onChange }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const factTypesStore = useFactTypesStore();
    const personFactsStore = usePersonFactsStore();
    const personLifeEventsStore = usePersonLifeEventsStore();
    const [eventTypes, setEventTypes] = useState<{ id: string; displayName: string }[]>([]);

    const [factFormOpen, setFactFormOpen] = useState(false);
    const [factForm, setFactForm] = useState({ factTypeId: '', detail: '', notes: '', sourceNotes: '' });
    const [editingFactId, setEditingFactId] = useState<string | null>(null);

    const [eventFormOpen, setEventFormOpen] = useState(false);
    const [eventForm, setEventForm] = useState({
      eventTypeId: '',
      eventDate: '',
      eventDatePrecision: 'day',
      eventPlace: '',
      detail: '',
      description: '',
      sourceNotes: ''
    });
    const [editingEventId, setEditingEventId] = useState<string | null>(null);

    useEffect(() => {
      factTypesStore.loadFactTypes();
      eventTypesApi.listEventTypes(true).then((res) => {
        if (res.success && res.data?.eventTypes) {
          setEventTypes(
            res.data.eventTypes.map((et) => ({
              id: et.id,
              displayName: et.displayName ?? et.slug ?? t('suggest.eventDisplayFallback')
            }))
          );
        }
      });
      personFactsStore.loadFactsForFamilyMember(familyMemberId);
      personLifeEventsStore.loadLifeEventsForFamilyMember(familyMemberId);
    }, [familyMemberId, factTypesStore, personFactsStore, personLifeEventsStore, t]);

    const existingFacts = personFactsStore.getFactsForFamilyMember(familyMemberId);
    const existingEvents = personLifeEventsStore.getLifeEventsForFamilyMember(familyMemberId);

    const isFactDeleted = (id: string) => value.factDeletes.includes(id);
    const isEventDeleted = (id: string) => value.lifeEventDeletes.includes(id);

    const getFactUpdate = (id: string) => value.factUpdates.find((u) => u.personFactId === id);
    const getEventUpdate = (id: string) => value.lifeEventUpdates.find((u) => u.lifeEventId === id);

    const resetFactForm = () => {
      setFactForm({ factTypeId: '', detail: '', notes: '', sourceNotes: '' });
      setEditingFactId(null);
      setFactFormOpen(false);
    };

    const resetEventForm = () => {
      setEventForm({
        eventTypeId: '',
        eventDate: '',
        eventDatePrecision: 'day',
        eventPlace: '',
        detail: '',
        description: '',
        sourceNotes: ''
      });
      setEditingEventId(null);
      setEventFormOpen(false);
    };

    const saveFactDraft = () => {
      if (!factForm.factTypeId) return;
      const factType = factTypesStore.getFactTypeById(factForm.factTypeId);
      const displayName = factType?.displayName ?? t('suggest.factDisplayFallback');

      if (editingFactId) {
        const existing = existingFacts.find((f) => f.id === editingFactId);
        const nextUpdates = value.factUpdates.filter((u) => u.personFactId !== editingFactId);
        nextUpdates.push({
          personFactId: editingFactId,
          factTypeId: factForm.factTypeId,
          factTypeDisplayName: displayName,
          detail: factForm.detail,
          notes: factForm.notes,
          sourceNotes: factForm.sourceNotes,
          originalFactTypeId: existing?.factTypeId ?? undefined,
          originalDetail: existing?.detail ?? undefined,
          originalNotes: existing?.notes ?? undefined,
          originalSourceNotes: existing?.sourceNotes ?? undefined
        });
        onChange({ ...value, factUpdates: nextUpdates });
      } else {
        onChange({
          ...value,
          factCreates: [
            ...value.factCreates,
            {
              id: generateUUID(),
              factTypeId: factForm.factTypeId,
              factTypeDisplayName: displayName,
              detail: factForm.detail,
              notes: factForm.notes,
              sourceNotes: factForm.sourceNotes
            }
          ]
        });
      }
      resetFactForm();
    };

    const saveEventDraft = () => {
      if (!eventForm.eventTypeId) return;
      const eventType = eventTypes.find((et) => et.id === eventForm.eventTypeId);
      const displayName = eventType?.displayName ?? t('suggest.lifeEventDisplayFallback');

      if (editingEventId) {
        const existing = existingEvents.find((e) => e.id === editingEventId);
        const nextUpdates = value.lifeEventUpdates.filter((u) => u.lifeEventId !== editingEventId);
        nextUpdates.push({
          lifeEventId: editingEventId,
          eventTypeId: eventForm.eventTypeId,
          eventTypeDisplayName: displayName,
          eventDate: eventForm.eventDate,
          eventDatePrecision: eventForm.eventDatePrecision,
          eventPlace: eventForm.eventPlace,
          detail: eventForm.detail,
          description: eventForm.description,
          sourceNotes: eventForm.sourceNotes,
          originalEventTypeId: existing?.eventTypeId ?? undefined,
          originalEventDate: existing?.eventDate ?? undefined,
          originalEventDatePrecision: existing?.eventDatePrecision ?? undefined,
          originalEventPlace: existing?.eventPlace ?? undefined,
          originalDetail: existing?.detail ?? undefined,
          originalDescription: existing?.description ?? undefined,
          originalSourceNotes: existing?.sourceNotes ?? undefined
        });
        onChange({ ...value, lifeEventUpdates: nextUpdates });
      } else {
        onChange({
          ...value,
          lifeEventCreates: [
            ...value.lifeEventCreates,
            {
              id: generateUUID(),
              eventTypeId: eventForm.eventTypeId,
              eventTypeDisplayName: displayName,
              eventDate: eventForm.eventDate,
              eventDatePrecision: eventForm.eventDatePrecision,
              eventPlace: eventForm.eventPlace,
              detail: eventForm.detail,
              description: eventForm.description,
              sourceNotes: eventForm.sourceNotes
            }
          ]
        });
      }
      resetEventForm();
    };

    return (
      <div className="suggest-facts-life-events">
        <div className="suggest-facts-life-events__section">
          <div className="suggest-facts-life-events__header">
            <h3 className="person-edit-form__section-title">{t('person.tabs.facts')}</h3>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                resetFactForm();
                setFactFormOpen(true);
              }}
            >
              <Plus width={13} height={13} />
              {t('suggest.proposeFact')}
            </button>
          </div>

          {factFormOpen && (
            <div className="person-facts__form suggest-facts-life-events__form">
              <div className="person-facts__form-row person-facts__form-row--type-detail">
                <div className="person-facts__form-field person-facts__form-field--type">
                  <label className="person-facts__form-label">{t('person.factType')} *</label>
                  <Select.Root
                    value={factForm.factTypeId}
                    onValueChange={(v) => setFactForm((prev) => ({ ...prev, factTypeId: v }))}
                    disabled={!!editingFactId}
                  >
                    <Select.Trigger className="form-select-trigger" aria-label={t('person.factType')}>
                      <Select.Value placeholder={t('suggest.selectTypePlaceholder')} />
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                        {factTypesStore.activeFactTypes.map((ft) => (
                          <Select.Item key={ft.id} value={ft.id} className="form-select-item">
                            <Select.ItemText>{ft.displayName}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                </div>
                <div className="person-facts__form-field person-facts__form-field--detail">
                  <label className="person-facts__form-label">{t('person.detail')}</label>
                  <input
                    type="text"
                    value={factForm.detail}
                    onChange={(e) => setFactForm((prev) => ({ ...prev, detail: e.target.value }))}
                    className="person-facts__form-input"
                  />
                </div>
              </div>
              <div className="person-facts__form-actions">
                <button type="button" className="person-facts__cancel-button" onClick={resetFactForm}>
                  {tCommon('cancel')}
                </button>
                <button type="button" className="person-facts__save-button" onClick={saveFactDraft}>
                  {editingFactId ? t('suggest.updateProposal') : t('suggest.addToProposal')}
                </button>
              </div>
            </div>
          )}

          <ul className="suggest-facts-life-events__list">
            {value.factCreates.map((fact) => (
              <li key={fact.id} className="suggest-facts-life-events__item">
                <strong>{fact.factTypeDisplayName}</strong>
                {fact.detail && <span> — {fact.detail}</span>}
                <span className="suggest-facts-life-events__badge">{t('suggest.badgeNew')}</span>
                <button
                  type="button"
                  className="suggest-facts-life-events__remove"
                  onClick={() =>
                    onChange({
                      ...value,
                      factCreates: value.factCreates.filter((f) => f.id !== fact.id)
                    })
                  }
                >
                  {tCommon('remove')}
                </button>
              </li>
            ))}
            {existingFacts.map((fact) => {
              if (isFactDeleted(fact.id)) {
                return (
                  <li key={fact.id} className="suggest-facts-life-events__item suggest-facts-life-events__item--muted">
                    <span>
                      {tCommon('remove')}{' '}
                      {factTypesStore.getFactTypeById(fact.factTypeId)?.displayName ?? t('suggest.factFallback')}
                    </span>
                    <button
                      type="button"
                      className="suggest-facts-life-events__remove"
                      onClick={() =>
                        onChange({
                          ...value,
                          factDeletes: value.factDeletes.filter((id) => id !== fact.id)
                        })
                      }
                    >
                      {t('suggest.undo')}
                    </button>
                  </li>
                );
              }
              const update = getFactUpdate(fact.id);
              const displayName = factTypesStore.getFactTypeById(update?.factTypeId ?? fact.factTypeId)?.displayName;
              const detail = update?.detail ?? fact.detail;
              return (
                <li key={fact.id} className="suggest-facts-life-events__item">
                  <strong>{displayName}</strong>
                  {detail && <span> — {detail}</span>}
                  {update && <span className="suggest-facts-life-events__badge">{t('suggest.badgeEdited')}</span>}
                  <button
                    type="button"
                    className="suggest-facts-life-events__action"
                    onClick={() => {
                      setEditingFactId(fact.id);
                      setFactForm({
                        factTypeId: update?.factTypeId ?? fact.factTypeId,
                        detail: update?.detail ?? fact.detail ?? '',
                        notes: update?.notes ?? fact.notes ?? '',
                        sourceNotes: update?.sourceNotes ?? fact.sourceNotes ?? ''
                      });
                      setFactFormOpen(true);
                    }}
                  >
                    {tCommon('edit')}
                  </button>
                  <button
                    type="button"
                    className="suggest-facts-life-events__remove"
                    onClick={() =>
                      onChange({
                        ...value,
                        factUpdates: value.factUpdates.filter((u) => u.personFactId !== fact.id),
                        factDeletes: [...value.factDeletes, fact.id]
                      })
                    }
                  >
                    {tCommon('remove')}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="suggest-facts-life-events__section">
          <div className="suggest-facts-life-events__header">
            <h3 className="person-edit-form__section-title">{t('person.tabs.lifeEvents')}</h3>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                resetEventForm();
                setEventFormOpen(true);
              }}
            >
              <Plus width={13} height={13} />
              {t('suggest.proposeEvent')}
            </button>
          </div>

          {eventFormOpen && (
            <div className="person-life-events__form suggest-facts-life-events__form">
              <div className="person-life-events__form-field">
                <label className="person-life-events__form-label">{t('person.eventType')} *</label>
                <Select.Root
                  value={eventForm.eventTypeId}
                  onValueChange={(v) => setEventForm((prev) => ({ ...prev, eventTypeId: v }))}
                  disabled={!!editingEventId}
                >
                  <Select.Trigger className="form-select-trigger" aria-label={t('person.eventType')}>
                    <Select.Value placeholder={t('suggest.selectTypePlaceholder')} />
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                      {eventTypes.map((et) => (
                        <Select.Item key={et.id} value={et.id} className="form-select-item">
                          <Select.ItemText>{et.displayName}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>
              <DateFieldWithPrecision
                id="suggest-event-date"
                label={t('person.eventDate')}
                value={eventForm.eventDate}
                precision={eventForm.eventDatePrecision as DatePrecision}
                onValueChange={(v) => setEventForm((prev) => ({ ...prev, eventDate: v }))}
                onPrecisionChange={(p) => setEventForm((prev) => ({ ...prev, eventDatePrecision: p }))}
                allowClearViaInput={false}
                layout="inline"
              />
              <div className="person-life-events__form-field">
                <label className="person-life-events__form-label">{t('person.place')}</label>
                <input
                  type="text"
                  value={eventForm.eventPlace}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, eventPlace: e.target.value }))}
                  className="person-life-events__form-input"
                />
              </div>
              <div className="person-life-events__form-actions">
                <button type="button" className="person-life-events__cancel-button" onClick={resetEventForm}>
                  {tCommon('cancel')}
                </button>
                <button type="button" className="person-life-events__save-button" onClick={saveEventDraft}>
                  {editingEventId ? t('suggest.updateProposal') : t('suggest.addToProposal')}
                </button>
              </div>
            </div>
          )}

          <ul className="suggest-facts-life-events__list">
            {value.lifeEventCreates.map((event) => (
              <li key={event.id} className="suggest-facts-life-events__item">
                <strong>{event.eventTypeDisplayName}</strong>
                {event.eventDate && <span> — {event.eventDate}</span>}
                <span className="suggest-facts-life-events__badge">{t('suggest.badgeNew')}</span>
                <button
                  type="button"
                  className="suggest-facts-life-events__remove"
                  onClick={() =>
                    onChange({
                      ...value,
                      lifeEventCreates: value.lifeEventCreates.filter((e) => e.id !== event.id)
                    })
                  }
                >
                  {tCommon('remove')}
                </button>
              </li>
            ))}
            {existingEvents.map((event) => {
              if (isEventDeleted(event.id)) {
                return (
                  <li key={event.id} className="suggest-facts-life-events__item suggest-facts-life-events__item--muted">
                    <span>
                      {tCommon('remove')}{' '}
                      {eventTypes.find((et) => et.id === event.eventTypeId)?.displayName ?? t('suggest.eventFallback')}
                    </span>
                    <button
                      type="button"
                      className="suggest-facts-life-events__remove"
                      onClick={() =>
                        onChange({
                          ...value,
                          lifeEventDeletes: value.lifeEventDeletes.filter((id) => id !== event.id)
                        })
                      }
                    >
                      {t('suggest.undo')}
                    </button>
                  </li>
                );
              }
              const update = getEventUpdate(event.id);
              const displayName = eventTypes.find(
                (et) => et.id === (update?.eventTypeId ?? event.eventTypeId)
              )?.displayName;
              const eventDate = update?.eventDate ?? event.eventDate;
              return (
                <li key={event.id} className="suggest-facts-life-events__item">
                  <strong>{displayName}</strong>
                  {eventDate && <span> — {formatDateForInput(eventDate) ?? eventDate}</span>}
                  {update && <span className="suggest-facts-life-events__badge">{t('suggest.badgeEdited')}</span>}
                  <button
                    type="button"
                    className="suggest-facts-life-events__action"
                    onClick={() => {
                      setEditingEventId(event.id);
                      setEventForm({
                        eventTypeId: update?.eventTypeId ?? event.eventTypeId,
                        eventDate: formatDateForInput(update?.eventDate ?? event.eventDate ?? undefined) ?? '',
                        eventDatePrecision: update?.eventDatePrecision ?? event.eventDatePrecision ?? 'day',
                        eventPlace: update?.eventPlace ?? event.eventPlace ?? '',
                        detail: update?.detail ?? event.detail ?? '',
                        description: update?.description ?? event.description ?? '',
                        sourceNotes: update?.sourceNotes ?? event.sourceNotes ?? ''
                      });
                      setEventFormOpen(true);
                    }}
                  >
                    {tCommon('edit')}
                  </button>
                  <button
                    type="button"
                    className="suggest-facts-life-events__remove"
                    onClick={() =>
                      onChange({
                        ...value,
                        lifeEventUpdates: value.lifeEventUpdates.filter((u) => u.lifeEventId !== event.id),
                        lifeEventDeletes: [...value.lifeEventDeletes, event.id]
                      })
                    }
                  >
                    {tCommon('remove')}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }
);

export default SuggestFactsLifeEventsSection;
